import React, { useEffect, useState, useRef } from 'react';
import { XIcon } from 'lucide-react';
import { motion } from 'framer-motion';
import { PTPData } from '../types/ptp';
interface PTPExportViewProps {
  data: PTPData;
  onClose: () => void;
  isUpdated?: boolean;
}
const A4_WIDTH = 595;
const A4_HEIGHT = 842;
export function PTPExportView({
  data,
  onClose,
  isUpdated = false
}: PTPExportViewProps) {
  const formNumber = 'PTP-2026-00847';
  const revision = isUpdated ? 'Rev. 1 (AI Updated)' : 'Rev. 0';
  const containerRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState(1);
  useEffect(() => {
    const updateScale = () => {
      if (!containerRef.current) return;
      const {
        clientWidth,
        clientHeight
      } = containerRef.current;
      const padding = 24;
      const availW = clientWidth - padding * 2;
      const availH = clientHeight - padding * 2;
      const scaleX = availW / A4_WIDTH;
      const scaleY = availH / A4_HEIGHT;
      setScale(Math.min(scaleX, scaleY, 1));
    };
    updateScale();
    window.addEventListener('resize', updateScale);
    return () => window.removeEventListener('resize', updateScale);
  }, []);
  return <motion.div initial={{
    opacity: 0
  }} animate={{
    opacity: 1
  }} exit={{
    opacity: 0
  }} className="absolute inset-0 z-[100] flex flex-col bg-neutral-800 rounded-lg overflow-hidden" data-id="element-8">
      <div className="flex items-center justify-between px-3 py-2.5 bg-neutral-900 border-b border-neutral-700 flex-shrink-0" data-id="element-9">
        <button onClick={onClose} className="flex items-center gap-1.5 px-2 py-1 rounded hover:bg-neutral-700 text-neutral-300 hover:text-white transition-colors text-xs font-medium" data-id="element-10">
          <XIcon className="w-4 h-4" data-id="element-11" />
          Close
        </button>
        <div className="bg-neutral-700 rounded px-3 py-1 text-xs text-neutral-400 truncate max-w-[200px]" data-id="element-12">
          {formNumber}.pdf
        </div>
      </div>

      <div ref={containerRef} className="flex-1 overflow-hidden bg-neutral-600 flex items-center justify-center p-3" data-id="element-13">
        <div style={{
        width: A4_WIDTH,
        height: A4_HEIGHT,
        transform: `scale(${scale})`,
        transformOrigin: 'center center'
      }} className="bg-white shadow-2xl overflow-y-auto flex-shrink-0" data-id="element-14">
          <div className="p-6 text-black" style={{
          fontFamily: 'Arial, Helvetica, sans-serif',
          fontSize: '9px',
          lineHeight: '1.35'
        }} data-id="element-15">
            {/* Header */}
            <div className="flex items-start justify-between mb-1 pb-1.5 border-b-2 border-black" data-id="element-16">
              <div data-id="element-17">
                <div className="mb-0.5" data-id="element-18">
                  <span style={{
                  fontSize: '14px',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#000000'
                }} data-id="element-19">
                    Bilt
                  </span>
                  <span style={{
                  fontSize: '14px',
                  fontWeight: 800,
                  letterSpacing: '-0.02em',
                  color: '#ca8a04'
                }} data-id="element-20">
                    On
                  </span>
                </div>
                <div className="text-[7px] text-neutral-500" data-id="element-21">
                  Safety Management System
                </div>
              </div>
              <div className="text-right" data-id="element-22">
                <div className="text-[7px] text-neutral-500" data-id="element-23">
                  Form No: {formNumber}
                </div>
                <div className="text-[7px] text-neutral-500" data-id="element-24">{revision}</div>
                <div className="text-[7px] text-neutral-500" data-id="element-25">Page 1 of 1</div>
              </div>
            </div>

            {/* Title */}
            <div className="text-center my-2.5" data-id="element-26">
              <h1 className="text-sm font-bold uppercase tracking-wide" data-id="element-27">
                Pre-Task Plan / Job Hazard Analysis
              </h1>
              <p className="text-[8px] text-neutral-500 mt-0.5" data-id="element-28">
                Daily Safety Planning Document
              </p>
            </div>

            {/* Project Information Table */}
            <table className="w-full border-collapse mb-3" style={{
            fontSize: '8px'
          }} data-id="element-29">
              <tbody data-id="element-30">
                <tr data-id="element-31">
                  <td className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 font-bold w-[70px]" data-id="element-32">
                    Project
                  </td>
                  <td className="border border-neutral-400 px-1.5 py-1" data-id="element-33">
                    {data.project}
                  </td>
                  <td className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 font-bold w-[70px]" data-id="element-34">
                    Date
                  </td>
                  <td className="border border-neutral-400 px-1.5 py-1 w-[90px]" data-id="element-35">
                    {data.date}
                  </td>
                </tr>
                <tr data-id="element-36">
                  <td className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 font-bold" data-id="element-37">
                    Location
                  </td>
                  <td className="border border-neutral-400 px-1.5 py-1" data-id="element-38">
                    {data.location}
                  </td>
                  <td className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 font-bold" data-id="element-39">
                    Foreman
                  </td>
                  <td className="border border-neutral-400 px-1.5 py-1" data-id="element-40">
                    ________________
                  </td>
                </tr>
                <tr data-id="element-41">
                  <td className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 font-bold" data-id="element-42">
                    Task
                  </td>
                  <td className="border border-neutral-400 px-1.5 py-1 font-semibold" colSpan={3} data-id="element-43">
                    Steel Beam Installation Using Mobile Crane
                  </td>
                </tr>
              </tbody>
            </table>

            {/* JHA Table */}
            <div className="mb-3" data-id="element-44">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-45">
                Section 1 — Job Steps, Hazards & Control Measures
              </div>
              <table className="w-full border-collapse" style={{
              fontSize: '8px'
            }} data-id="element-46">
                <thead data-id="element-47">
                  <tr data-id="element-48">
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left w-[20px]" data-id="element-49">
                      #
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left w-[28%]" data-id="element-50">
                      Job Step
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left w-[28%]" data-id="element-51">
                      Potential Hazard(s)
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left" data-id="element-52">
                      Control Measure(s)
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-53">
                  {data.steps.map((step, i) => {
                  const rowSpan = Math.max(step.hazards.length, 1);
                  return step.hazards.length === 0 ? <tr key={i} data-id="element-54">
                        <td className="border border-neutral-400 px-1.5 py-1 text-center font-bold align-top" data-id="element-55">
                          {i + 1}
                        </td>
                        <td className="border border-neutral-400 px-1.5 py-1 align-top" data-id="element-56">
                          {step.description}
                        </td>
                        <td className="border border-neutral-400 px-1.5 py-1 text-neutral-400 italic" data-id="element-57">
                          None identified
                        </td>
                        <td className="border border-neutral-400 px-1.5 py-1" data-id="element-58">
                          —
                        </td>
                      </tr> : step.hazards.map((h, j) => <tr key={`${i}-${j}`} data-id="element-59">
                          {j === 0 && <>
                              <td className="border border-neutral-400 px-1.5 py-1 text-center font-bold align-top" rowSpan={rowSpan} data-id="element-60">
                                {i + 1}
                              </td>
                              <td className="border border-neutral-400 px-1.5 py-1 align-top" rowSpan={rowSpan} data-id="element-61">
                                {step.description}
                              </td>
                            </>}
                          <td className={`border border-neutral-400 px-1.5 py-1 align-top ${h.isNew ? 'font-semibold' : ''}`} data-id="element-62">
                            <div className="flex items-start gap-0.5" data-id="element-63">
                              <span data-id="element-64">{h.hazard}</span>
                              {h.riskLevel && <span className={`text-[6px] font-bold px-0.5 rounded flex-shrink-0 ${h.riskLevel === 'HIGH' ? 'text-red-700 bg-red-100' : h.riskLevel === 'MEDIUM' ? 'text-amber-700 bg-amber-100' : 'text-green-700 bg-green-100'}`} data-id="element-65">
                                  {h.riskLevel}
                                </span>}
                              {h.isNew && <span className="text-[6px] font-bold bg-neutral-200 px-0.5 rounded flex-shrink-0" data-id="element-66">
                                  AI
                                </span>}
                            </div>
                          </td>
                          <td className="border border-neutral-400 px-1.5 py-1 align-top" data-id="element-67">
                            {h.controls.map((c, k) => <div key={k} className="flex items-start gap-0.5 mb-0.5" data-id="element-68">
                                <span className="flex-shrink-0" data-id="element-69">•</span>
                                <span data-id="element-70">{c}</span>
                              </div>)}
                          </td>
                        </tr>);
                })}
                </tbody>
              </table>
            </div>

            {/* Crew Assignment */}
            <div className="mb-3" data-id="element-71">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-72">
                Section 2 — Crew Assignment ({data.crewSize} Workers)
              </div>
              <table className="w-full border-collapse" style={{
              fontSize: '8px'
            }} data-id="element-73">
                <thead data-id="element-74">
                  <tr data-id="element-75">
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left w-[90px]" data-id="element-76">
                      Role
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left w-[90px]" data-id="element-77">
                      Name
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-1 text-left" data-id="element-78">
                      Responsibility
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-79">
                  {data.crewRoles.map((role, i) => <tr key={i} data-id="element-80">
                      <td className="border border-neutral-400 px-1.5 py-1 font-semibold" data-id="element-81">
                        {role.role}
                      </td>
                      <td className="border border-neutral-400 px-1.5 py-1 text-neutral-400" data-id="element-82">
                        ____________
                      </td>
                      <td className="border border-neutral-400 px-1.5 py-1" data-id="element-83">
                        {role.responsibility}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* PPE */}
            <div className="mb-3" data-id="element-84">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-85">
                Section 3 — Required PPE
              </div>
              <div className="border border-neutral-400 px-2 py-1.5 flex flex-wrap gap-x-4 gap-y-0.5" style={{
              fontSize: '8px'
            }} data-id="element-86">
                {data.ppeControls.map((ppe, i) => <span key={i} className="flex items-center gap-0.5" data-id="element-87">
                    <span className="inline-block w-2.5 h-2.5 border border-neutral-500 bg-neutral-100 text-center text-[6px] leading-[10px] font-bold" data-id="element-88">
                      ✓
                    </span>
                    {ppe}
                  </span>)}
                {['Safety glasses', 'Steel toe boots', 'Work gloves', 'Fall protection harness'].map((ppe, i) => <span key={`extra-${i}`} className="flex items-center gap-0.5 text-neutral-400" data-id="element-89">
                    <span className="inline-block w-2.5 h-2.5 border border-neutral-300 bg-white" data-id="element-90"></span>
                    {ppe}
                  </span>)}
              </div>
            </div>

            {/* Equipment */}
            <div className="mb-3" data-id="element-91">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-92">
                Section 4 — Equipment & Tools
              </div>
              <div className="border border-neutral-400 px-2 py-1.5" style={{
              fontSize: '8px'
            }} data-id="element-93">
                {data.equipment.join('  •  ')}
              </div>
            </div>

            {/* Safety Checks */}
            <div className="mb-3" data-id="element-94">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-95">
                Section 5 — Pre-Work Safety Verification
              </div>
              <table className="w-full border-collapse" style={{
              fontSize: '8px'
            }} data-id="element-96">
                <tbody data-id="element-97">
                  {data.safetyChecks.map((check, i) => <tr key={i} data-id="element-98">
                      <td className="border border-neutral-400 px-1.5 py-0.5 w-[20px] text-center" data-id="element-99">
                        <span className="inline-block w-2.5 h-2.5 border border-neutral-500 bg-neutral-100 text-center text-[6px] leading-[10px] font-bold" data-id="element-100">
                          ✓
                        </span>
                      </td>
                      <td className="border border-neutral-400 px-1.5 py-0.5" data-id="element-101">
                        {check}
                      </td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* AI Meeting Notes */}
            {isUpdated && data.meetingScore && <div className="mb-3" data-id="element-102">
                <div className="bg-neutral-700 text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-103">
                  AI Meeting Review Notes
                </div>
                <div className="border border-neutral-400 px-2 py-1.5" style={{
              fontSize: '8px'
            }} data-id="element-104">
                  <div className="mb-0.5" data-id="element-105">
                    <strong data-id="element-106">Meeting Score:</strong> {data.meetingScore} / 100 —
                    Needs Improvement
                  </div>
                  <div data-id="element-107">
                    <strong data-id="element-108">Primary Gap:</strong> Crew participation and missing
                    review of wind conditions and emergency response procedures.
                  </div>
                </div>
              </div>}

            {/* Signatures */}
            <div className="mb-3" data-id="element-109">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-110">
                {isUpdated ? 'Section 7' : 'Section 6'} — Crew Acknowledgment &
                Signatures
              </div>
              <div className="border border-neutral-400 px-2 py-1 text-[7px] text-neutral-500 italic" data-id="element-111">
                By signing below, each crew member confirms they have reviewed
                and understand this Pre-Task Plan.
              </div>
              <table className="w-full border-collapse" style={{
              fontSize: '8px'
            }} data-id="element-112">
                <thead data-id="element-113">
                  <tr data-id="element-114">
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-0.5 text-left w-[20px]" data-id="element-115">
                      #
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-0.5 text-left" data-id="element-116">
                      Name
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-0.5 text-left" data-id="element-117">
                      Role
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-0.5 text-left w-[120px]" data-id="element-118">
                      Signature
                    </th>
                    <th className="border border-neutral-400 bg-neutral-100 px-1.5 py-0.5 text-left w-[45px]" data-id="element-119">
                      Time
                    </th>
                  </tr>
                </thead>
                <tbody data-id="element-120">
                  {data.crewRoles.map((role, i) => <tr key={i} data-id="element-121">
                      <td className="border border-neutral-400 px-1.5 py-2 text-center" data-id="element-122">
                        {i + 1}
                      </td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-123"></td>
                      <td className="border border-neutral-400 px-1.5 py-2 text-neutral-500" data-id="element-124">
                        {role.role}
                      </td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-125"></td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-126"></td>
                    </tr>)}
                  {[1, 2].map((_, i) => <tr key={`blank-${i}`} data-id="element-127">
                      <td className="border border-neutral-400 px-1.5 py-2 text-center text-neutral-300" data-id="element-128">
                        {data.crewRoles.length + i + 1}
                      </td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-129"></td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-130"></td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-131"></td>
                      <td className="border border-neutral-400 px-1.5 py-2" data-id="element-132"></td>
                    </tr>)}
                </tbody>
              </table>
            </div>

            {/* Supervisor Approval */}
            <div className="mb-4" data-id="element-133">
              <div className="bg-black text-white px-1.5 py-0.5 font-bold text-[8px] uppercase tracking-wider" data-id="element-134">
                Supervisor / Site Manager Approval
              </div>
              <div className="border border-neutral-400 p-2 grid grid-cols-2 gap-3" style={{
              fontSize: '8px'
            }} data-id="element-135">
                <div data-id="element-136">
                  <div className="text-neutral-500 mb-3" data-id="element-137">
                    Name: ________________________
                  </div>
                  <div className="text-neutral-500" data-id="element-138">
                    Title: ________________________
                  </div>
                </div>
                <div data-id="element-139">
                  <div className="text-neutral-500 mb-3" data-id="element-140">
                    Signature: ____________________
                  </div>
                  <div className="text-neutral-500" data-id="element-141">
                    Date / Time: __________________
                  </div>
                </div>
              </div>
            </div>

            {/* Footer */}
            <div className="border-t border-neutral-300 pt-1.5 flex justify-between text-[6px] text-neutral-400" data-id="element-142">
              <span data-id="element-143">
                {formNumber} | {revision}
              </span>
              <span data-id="element-144">CONFIDENTIAL — For authorized project personnel only</span>
              <span data-id="element-145">Generated: {data.date}</span>
            </div>
          </div>
        </div>
      </div>
    </motion.div>;
}